import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useSelector } from "react-redux";
import { selectIsLoggingOut } from "../../store/authUserSlice";
import Loader from "../../common/loader";
import { renewOidcSession } from "../../interceptors/oidcSilentRenewal";
import userManager from "../../auth/userManager";

const getStoredRefreshToken = () => {
  try {
    const stores = [localStorage, sessionStorage];
    for (const store of stores) {
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (key && key.startsWith("oidc.user:")) {
          const item = store.getItem(key);
          if (item) {
            const parsed = JSON.parse(item);
            if (parsed && parsed.refresh_token) {
              return parsed.refresh_token;
            }
          }
        }
      }
    }
  } catch (e) {
    // ignore
  }
  return null;
};

const hasStoredOidcUser = () => {
  try {
    const stores = [localStorage, sessionStorage];
    const now = Math.floor(Date.now() / 1000);
    for (const store of stores) {
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (key && key.startsWith("oidc.user:")) {
          const item = store.getItem(key);
          if (item) {
            const parsed = JSON.parse(item);
            if (parsed && parsed.access_token) {
              if (parsed.expires_at && now >= parsed.expires_at && !parsed.refresh_token) {
                continue;
              }
              return true;
            }
          }
        }
      }
    }
  } catch (e) {
    // ignore
  }
  return false;
};

/**
 * OidcPrivateRoute - Protects routes on the OIDC library's real session
 * state (see docs/specs/0001-oidc-migration.md).
 */
const OidcPrivateRoute = ({ children }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggingOut = useSelector(selectIsLoggingOut);

  const [isRenewing, setIsRenewing] = useState(false);
  const [renewalAttempted, setRenewalAttempted] = useState(false);

  const isSessionRestoring = auth.isLoading && hasStoredOidcUser();

  useEffect(() => {
    // If not authenticated, but we have a stored refresh_token that hasn't been tried yet:
    if (!auth.isLoading && !auth.isAuthenticated && !isLoggingOut && !renewalAttempted) {
      const refreshToken = getStoredRefreshToken();
      if (refreshToken) {
        setIsRenewing(true);
        renewOidcSession()
          .then(() => {
            setIsRenewing(false);
            setRenewalAttempted(true);
          })
          .catch(async () => {
            try {
              await userManager.removeUser();
            } catch (e) {}
            setIsRenewing(false);
            setRenewalAttempted(true);
          });
        return;
      } else {
        setRenewalAttempted(true);
      }
    }

    // Wait for session restoration from sessionStorage / auth loading / active logout / in-progress renewal
    if (auth.isLoading || auth.isAuthenticated || isLoggingOut || isSessionRestoring || isRenewing) {
      return;
    }

    if (!auth.isAuthenticated && (renewalAttempted || !getStoredRefreshToken())) {
      navigate("/signin", { replace: true });
    }
  }, [
    auth.isLoading,
    auth.isAuthenticated,
    isLoggingOut,
    isSessionRestoring,
    isRenewing,
    renewalAttempted,
    navigate,
    location.pathname,
    location.search,
  ]);

  if (isLoggingOut || auth.isLoading || isSessionRestoring || isRenewing) {
    return <Loader />;
  }

  if (!auth.isAuthenticated) {
    navigate("/signin", { replace: true });
    return <Loader />;
  }

  return children;
};

export default OidcPrivateRoute;
